// /Users/pramod/Desktop/school-erp/frontend/src/Tenant-Admin/WebsiteManager/sections/InfrastructureSection.js

import React, { useState, useEffect } from 'react';
import { FiSave, FiPlus, FiTrash2, FiUpload, FiX, FiImage, FiEdit2 } from 'react-icons/fi';
import api from '../../../api';

// Toast Notification Component
const Toast = ({ message, type = 'success', onClose }) => {
  const bgColors = {
    success: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    error: 'bg-rose-50 border-rose-200 text-rose-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800'
  };

  return (
    <div className={`fixed bottom-4 right-4 z-50 max-w-sm w-full rounded-xl border p-4 shadow-lg animate-in fade-in slide-in-from-bottom-4 duration-200 ${bgColors[type]}`}>
      <div className="flex items-start justify-between">
        <p className="text-sm font-semibold">{message}</p>
        <button onClick={onClose} className="flex-shrink-0 ml-4 text-slate-400 hover:text-slate-600">
          <FiX className="h-4 w-4 stroke-[2.5]" />
        </button>
      </div>
    </div>
  );
};

// Simple Infrastructure Item Component
const InfrastructureItem = ({ item, index, onUpdate, onDelete, onImageUpload, uploading }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(item);

  // Resolve image URL
  const resolveAssetUrl = (urlPath) => {
    if (!urlPath) return '';
    if (typeof urlPath === 'string' && (urlPath.includes('tenants%2Fnull') || urlPath.includes('tenants/null'))) {
      return '';
    }
    const base = (api?.defaults?.baseURL || '').replace(/\/+$/, '') || (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
    if (/^https?:\/\//i.test(urlPath)) return urlPath;
    if (urlPath.startsWith('/')) return base ? `${base}${urlPath}` : urlPath;
    return urlPath;
  };

  const resolvedImage = resolveAssetUrl(item.image);

  const handleSaveEdit = () => {
    onUpdate(index, editData);
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditData(item);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="border border-blue-200 bg-blue-50/30 rounded-xl p-4 shadow-sm space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold text-slate-600 mb-1">Title *</label>
            <input
              type="text"
              value={editData.title || ''}
              onChange={(e) => setEditData({ ...editData, title: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-white focus:border-blue-500"
              placeholder="Facility name"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold text-slate-600 mb-1">Description</label>
            <textarea
              value={editData.description || ''}
              onChange={(e) => setEditData({ ...editData, description: e.target.value })}
              rows="2"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-white focus:border-blue-500 resize-y"
              placeholder="Brief description of the facility"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold text-slate-600 mb-1">Image Caption</label>
            <input
              type="text"
              value={editData.imageCaption || ''}
              onChange={(e) => setEditData({ ...editData, imageCaption: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-white focus:border-blue-500"
              placeholder="Caption for the image"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Category</label>
            <input
              type="text"
              value={editData.category || ''}
              onChange={(e) => setEditData({ ...editData, category: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-white focus:border-blue-500"
              placeholder="e.g., Lab, Sports, Library"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Display Order Index</label>
            <input
              type="number"
              value={editData.order || 0}
              onChange={(e) => setEditData({ ...editData, order: parseInt(e.target.value) || 0 })}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-white focus:border-blue-500"
              placeholder="Display order"
            />
          </div>
          
          <div className="sm:col-span-2 bg-white p-3 rounded-xl border border-slate-200">
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Facility Photo</label>
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 rounded-xl border border-slate-200 flex items-center justify-center overflow-hidden bg-slate-50 shadow-inner relative group">
                  {resolvedImage ? (
                    <img src={resolvedImage} alt={editData.title || "Facility"} className="w-full h-full object-cover" />
                  ) : (
                    <FiImage className="h-5 w-5 text-slate-400" />
                  )}
                </div>
              </div>
              <div className="flex-1 text-left">
                <input
                  type="file"
                  id={`image-edit-${index}`}
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => onImageUpload(e, index)}
                  className="hidden"
                />
                <label
                  htmlFor={`image-edit-${index}`}
                  className="inline-flex items-center px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 cursor-pointer shadow-sm transition-all select-none active:scale-95"
                >
                  <FiUpload className="mr-1.5 h-3.5 w-3.5 text-slate-500" />
                  {uploading ? 'Uploading...' : (resolvedImage ? 'Replace Image' : 'Upload Image')}
                </label>
                {resolvedImage && (
                  <button
                    type="button"
                    onClick={() => setEditData({ ...editData, image: '', imageKey: '' })}
                    className="ml-3 text-rose-500 hover:text-rose-700 font-semibold text-xs transition-colors"
                  >
                    Remove
                  </button>
                )}
                <p className="text-[10px] text-slate-400 mt-1">Max file boundary: 2MB (JPG, PNG, WEBP)</p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-2 border-t border-slate-200 pt-3">
          <button
            type="button"
            onClick={handleCancelEdit}
            className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSaveEdit}
            className="px-4 py-2 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 shadow-sm transition-all"
          >
            Apply Changes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-slate-200 rounded-xl p-4 bg-white shadow-sm hover:shadow-md transition-all">
      <div className="flex flex-col sm:flex-row gap-4 items-start">
        {/* Thumbnail preview segment */}
        <div className="flex-shrink-0 mx-auto sm:mx-0">
          <div className="w-24 h-24 rounded-xl border border-slate-200 flex items-center justify-center overflow-hidden bg-slate-50 shadow-inner">
            {resolvedImage ? (
              <img src={resolvedImage} alt={item.title || "Facility"} className="w-full h-full object-cover" />
            ) : (
              <FiImage className="h-7 w-7 text-slate-300" />
            )}
          </div>
        </div>

        {/* Informative Block parameters */}
        <div className="flex-1 min-w-0 w-full">
          <div className="flex items-start justify-between w-full">
            <div>
              <h4 className="text-base font-bold text-slate-900 truncate">{item.title || 'Untitled Facility'}</h4>
              {item.category && (
                <span className="inline-block mt-1 px-2.5 py-0.5 bg-blue-50 text-blue-700 text-xs font-semibold rounded-full border border-blue-100">
                  {item.category}
                </span>
              )}
            </div>
            <div className="flex items-center space-x-1 ml-4">
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="text-slate-400 hover:text-blue-600 p-1.5 rounded-lg hover:bg-slate-50 transition-colors"
                title="Edit Card"
              >
                <FiEdit2 className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => onDelete(index)}
                className="text-slate-400 hover:text-rose-600 p-1.5 rounded-lg hover:bg-slate-50 transition-colors"
                title="Delete Card"
              >
                <FiTrash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
          {item.description && (
            <p className="text-sm text-slate-600 mt-1.5 line-clamp-2 leading-relaxed">{item.description}</p>
          )}
          {item.imageCaption && (
            <p className="text-xs text-slate-400 mt-1 italic font-medium">"{item.imageCaption}"</p>
          )}
          <div className="flex items-center space-x-3 mt-3 pt-2.5 border-t border-slate-100 text-[11px] font-semibold text-slate-400">
            <span>Index Order: {item.order || 0}</span>
            <span>•</span>
            <span className={item.isActive !== false ? 'text-emerald-600' : 'text-slate-400'}>
              {item.isActive !== false ? 'Active Status' : 'Inactive Status'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Infrastructure Section Component
const InfrastructureSection = ({ websiteData, handleInputChange, handleSave, tenantId }) => {
  const infrastructure = websiteData?.infrastructure || { items: [] };

  // Local state vectors to isolate layout edits purely in memory
  const [localTitle, setLocalTitle] = useState(infrastructure.sectionTitle || '');
  const [localSubtitle, setLocalSubtitle] = useState(infrastructure.sectionSubtitle || '');
  const [localDisplayStyle, setLocalDisplayStyle] = useState(infrastructure.displayStyle || 'grid');
  const [localCheckboxes, setLocalCheckboxes] = useState({
    showFeatures: infrastructure.showFeatures !== false,
    showCaptions: infrastructure.showCaptions !== false
  });
  const [items, setItems] = useState(infrastructure.items || []);

  const [uploading, setUploading] = useState({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [newItem, setNewItem] = useState({
    title: '',
    description: '',
    imageCaption: '',
    category: '',
    order: 0,
    isActive: true
  });
  const [showAddForm, setShowAddForm] = useState(false);

  // Sync isolated memory allocations dynamically only on component mounting parameters
  useEffect(() => {
    console.log('[InfrastructureSection] Syncing local inputs layout configurations with source baseline properties.');
    setLocalTitle(infrastructure.sectionTitle || '');
    setLocalSubtitle(infrastructure.sectionSubtitle || '');
    setLocalDisplayStyle(infrastructure.displayStyle || 'grid');
    setLocalCheckboxes({
      showFeatures: infrastructure.showFeatures !== false,
      showCaptions: infrastructure.showCaptions !== false
    });
    setItems(infrastructure.items || []);
  }, [infrastructure.items, infrastructure.sectionTitle, infrastructure.sectionSubtitle, infrastructure.displayStyle, infrastructure.showFeatures, infrastructure.showCaptions, tenantId]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleToggle = () => {
    handleInputChange('infrastructure', 'isActive', !infrastructure.isActive);
  };

  const handleGlobalFieldChange = (field, value) => {
    console.log(`[InfrastructureSection] Shifting parameter configuration locally: Key [${field}]`);
    if (field === 'sectionTitle') setLocalTitle(value);
    if (field === 'sectionSubtitle') setLocalSubtitle(value);
    if (field === 'displayStyle') setLocalDisplayStyle(value);
  };

  const handleLocalCheckboxToggle = (key) => {
    setLocalCheckboxes(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Add new item structural changes updated down instantly
  const handleAddItem = () => {
    if (!newItem.title.trim()) {
      showToast('Please enter a title', 'error');
      return;
    }

    const item = {
      _id: Date.now().toString(),
      ...newItem,
      image: '',
      imageKey: ''
    };

    const updatedItems = [...items, item];
    setItems(updatedItems);
    handleInputChange('infrastructure', 'items', updatedItems);
    setNewItem({ title: '', description: '', imageCaption: '', category: '', order: 0, isActive: true });
    setShowAddForm(false);
    showToast('Facility added successfully', 'success');
  };

  // Update item array change actions updated instantly
  const handleUpdateItem = (index, updatedData) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], ...updatedData };
    setItems(updatedItems);
    handleInputChange('infrastructure', 'items', updatedItems);
    showToast('Facility updated successfully', 'success');
  };

  // Delete item array change actions updated instantly
  const handleDeleteItem = (index) => {
    if (!window.confirm('Are you sure you want to delete this facility?')) return;
    const updatedItems = items.filter((_, i) => i !== index);
    setItems(updatedItems);
    handleInputChange('infrastructure', 'items', updatedItems);
    showToast('Facility deleted', 'success');
  };

  const handleImageUpload = async (event, index) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      showToast('Image size must be less than 2MB', 'error');
      event.target.value = '';
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showToast('Invalid file type. Please upload an image.', 'error');
      event.target.value = '';
      return;
    }

    // Set immediate client-side preview string
    const localBlobPreview = URL.createObjectURL(file);
    const previewItems = [...items];
    previewItems[index] = { ...previewItems[index], image: localBlobPreview };
    setItems(previewItems);

    setUploading(prev => ({ ...prev, [index]: true }));

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('section', 'infrastructure');
      formData.append('fieldName', 'image');

      const response = await api.post(`/api/website/${tenantId}/upload/infrastructure`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        const fileUrl = response.data.data?.url || response.data.data;
        const updatedItems = [...items];
        updatedItems[index] = { ...updatedItems[index], image: fileUrl };
        setItems(updatedItems);
        handleInputChange('infrastructure', 'items', updatedItems);
        showToast('Image uploaded successfully', 'success');
      } else {
        showToast(response.data.message || 'Upload failed', 'error');
      }
    } catch (error) {
      console.error('Upload error:', error);
      showToast(error.response?.data?.message || 'Failed to upload image', 'error');
    } finally {
      setUploading(prev => ({ ...prev, [index]: false }));
      event.target.value = '';
    }
  };

  const handleSaveClick = async () => {
    setSaving(true);
    console.log('[InfrastructureSection] Sending final compiled payload to parent save handlers.');
    
    try {
      // Synchronously match child fields parameters to parent global trees map snapshots
      await handleInputChange('infrastructure', 'sectionTitle', localTitle);
      await handleInputChange('infrastructure', 'sectionSubtitle', localSubtitle);
      await handleInputChange('infrastructure', 'displayStyle', localDisplayStyle);
      await handleInputChange('infrastructure', 'showFeatures', localCheckboxes.showFeatures);
      await handleInputChange('infrastructure', 'showCaptions', localCheckboxes.showCaptions);
      await handleInputChange('infrastructure', 'items', items);

      const payload = {
        ...infrastructure,
        sectionTitle: localTitle,
        sectionSubtitle: localSubtitle,
        displayStyle: localDisplayStyle,
        showFeatures: localCheckboxes.showFeatures,
        showCaptions: localCheckboxes.showCaptions,
        items: items
      };
      
      await handleSave('infrastructure', payload);
      showToast('Infrastructure saved successfully', 'success');
    } catch (error) {
      console.error('Failed to save infrastructure section:', error);
      showToast('Failed to save infrastructure', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 mx-2 sm:mx-0 bg-slate-50 rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
      
      {/* Top Action Header row section */}
      <div className="bg-white p-4 sm:p-6 border-b border-slate-200">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900 tracking-tight">Infrastructure & Facilities</h2>
            <p className="text-sm text-slate-500 mt-0.5">Showcase campus facilities and state-of-the-art infrastructure setups</p>
          </div>
          
          <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:space-x-3">
            <button
              type="button"
              onClick={handleToggle}
              className={`flex items-center justify-center px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                infrastructure.isActive !== false
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'bg-slate-100 text-slate-600 border border-slate-200'
              }`}
            >
              {infrastructure.isActive !== false ? (
                <>
                  <span className="w-2 h-2 rounded-full bg-emerald-500 mr-2" />
                  Active
                </>
              ) : (
                <>
                  <span className="w-2 h-2 rounded-full bg-slate-400 mr-2" />
                  Inactive
                </>
              )}
            </button>

            <button
              type="button"
              onClick={handleSaveClick}
              disabled={saving}
              className={`flex items-center justify-center px-5 py-2.5 rounded-xl text-sm font-semibold shadow-sm transition-all ${
                saving 
                  ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.98]'
              }`}
            >
              <FiSave className="mr-2 h-4 w-4" />
              {saving ? 'Saving...' : 'Save All'}
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-6">
        
        {/* Settings Configuration Fields layout card */}
        <div className="bg-white rounded-xl p-4 sm:p-5 border border-slate-200/80 shadow-sm space-y-4">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Section Settings</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Section Title</label>
              <input
                type="text"
                value={localTitle}
                onChange={(e) => handleGlobalFieldChange('sectionTitle', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                placeholder="Campus Infrastructure"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Section Subtitle</label>
              <input
                type="text"
                value={localSubtitle}
                onChange={(e) => handleGlobalFieldChange('sectionSubtitle', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                placeholder="State-of-the-art facilities"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t border-slate-100 items-end">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Display Layout Alignment</label>
              <select
                value={localDisplayStyle}
                onChange={(e) => handleGlobalFieldChange('displayStyle', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none appearance-none"
              >
                <option value="grid">Grid Matrix</option>
                <option value="list">Vertical Row List</option>
                <option value="carousel">Carousel View Slider</option>
              </select>
            </div>

            <div className="md:col-span-2 bg-slate-50 rounded-xl p-3 border border-slate-150 grid grid-cols-2 gap-3">
              {[
                { label: 'Show Features', key: 'showFeatures', checked: localCheckboxes.showFeatures },
                { label: 'Show Captions', key: 'showCaptions', checked: localCheckboxes.showCaptions },
              ].map((checkbox) => (
                <label key={checkbox.key} className="flex items-center space-x-2.5 cursor-pointer group select-none">
                  <input
                    type="checkbox"
                    checked={checkbox.checked}
                    onChange={() => handleLocalCheckboxToggle(checkbox.key)}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-all border ${
                    checkbox.checked 
                      ? 'bg-blue-600 border-blue-600 text-white' 
                      : 'bg-white border-slate-300 text-transparent group-hover:border-slate-400'
                  }`}>
                    <span className={`w-2 h-2 rounded-sm bg-white ${checkbox.checked ? 'block' : 'hidden'}`} />
                  </div>
                  <span className="text-xs font-medium text-slate-600 group-hover:text-slate-900 transition-colors">
                    {checkbox.label}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Dynamic Mapping listing rows container */}
        <div className="space-y-4">
          <div className="flex items-center justify-between px-1">
            <h3 className="text-sm font-bold text-slate-800">Facilities ({items.length})</h3>
            <button
              type="button"
              onClick={() => setShowAddForm(!showAddForm)}
              className="inline-flex items-center px-4 py-2 bg-slate-900 text-white text-xs font-bold rounded-xl hover:bg-slate-800 transition-all shadow-sm active:scale-[0.98]"
            >
              <FiPlus className="mr-1.5 h-4 w-4 stroke-[3]" />
              Add Facility
            </button>
          </div>

          {/* New Item Block Addition Sub-Form section */}
          {showAddForm && (
            <div className="border border-blue-200 bg-blue-50/20 rounded-2xl p-4 sm:p-5 shadow-sm space-y-4 animate-in fade-in zoom-in-95 duration-150">
              <div className="flex items-center space-x-2 pb-2 border-b border-slate-200/60">
                <div className="p-1 bg-blue-50 text-blue-600 rounded-md">
                  <FiPlus className="w-4 h-4 stroke-[3]" />
                </div>
                <h4 className="text-sm font-bold text-slate-800">Create Campus Facility Card</h4>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Title *</label>
                  <input
                    type="text"
                    value={newItem.title}
                    onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-white focus:border-blue-500"
                    placeholder="Facility name (e.g., Central Library)"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Description</label>
                  <textarea
                    value={newItem.description}
                    onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                    rows="2"
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-white focus:border-blue-500 resize-y"
                    placeholder="Brief description of characteristics and availability metrics..."
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Image Caption</label>
                  <input
                    type="text"
                    value={newItem.imageCaption}
                    onChange={(e) => setNewItem({ ...newItem, imageCaption: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-white focus:border-blue-500"
                    placeholder="Image caption footer text"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Category</label>
                  <input
                    type="text"
                    value={newItem.category}
                    onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-white focus:border-blue-500"
                    placeholder="e.g., Lab, Sports, Library"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Display Index Order</label>
                  <input
                    type="number"
                    value={newItem.order}
                    onChange={(e) => setNewItem({ ...newItem, order: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-white focus:border-blue-500"
                    placeholder="Display order"
                  />
                </div>
                <div className="flex items-end pl-1 pb-2">
                  <label className="flex items-center space-x-2.5 cursor-pointer select-none group">
                    <input
                      type="checkbox"
                      checked={newItem.isActive !== false}
                      onChange={(e) => setNewItem({ ...newItem, isActive: e.target.checked })}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-all border ${
                      newItem.isActive !== false 
                        ? 'bg-blue-600 border-blue-600 text-white' 
                        : 'bg-white border-slate-300 text-transparent group-hover:border-slate-400'
                    }`}>
                      <span className="w-2 h-2 rounded-sm bg-white block" />
                    </div>
                    <span className="text-xs font-semibold text-slate-600 group-hover:text-slate-900 transition-colors">Visible Active Card</span>
                  </label>
                </div>
              </div>
              
              <div className="flex justify-end space-x-2 border-t border-slate-200 pt-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setNewItem({ title: '', description: '', imageCaption: '', category: '', order: 0, isActive: true });
                  }}
                  className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddItem}
                  className="px-4 py-2 bg-slate-900 text-white text-xs font-bold rounded-lg hover:bg-slate-800 shadow-sm transition-all"
                >
                  Insert Facility
                </button>
              </div>
            </div>
          )}

          {/* Empty Placeholder state layout cards maps definitions */}
          {items.length === 0 ? (
            <div className="text-center py-12 bg-white border border-slate-200 rounded-xl shadow-xs">
              <FiImage className="h-10 w-10 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-700 font-bold text-sm">No facilities added yet</p>
              <p className="text-xs text-slate-400 mt-0.5">Click "Add Facility" to showcase your campus infrastructure parameters.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((item, index) => (
                <InfrastructureItem
                  key={item._id || index}
                  item={item}
                  index={index}
                  onUpdate={handleUpdateItem}
                  onDelete={handleDeleteItem}
                  onImageUpload={handleImageUpload}
                  uploading={uploading[index]}
                  tenantId={tenantId}
                />
              ))}
            </div>
          )}
        </div>

      </div>

      {/* Toast Notification Container Wrapper block */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default InfrastructureSection;